import { Button, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import type { Moment } from 'moment';

import type { ClientService } from '../../../shared/api/ClientService';
import type { LocaleObj } from '../../../locale/LocalizedStrings';
import CommandStatusTag from './CommandStatusTag';
import InfoLabel from './InfoLabel';
import { COMMAND_COLUMN_WIDTH, EMPTY_VALUE } from './constants';
import type { CommandStatus, ICommandSort, SortColumnKey } from './types';
import { formatDateTime } from './utils';

type Command = ClientService.IntegrationCommandDto;

interface IOptions {
  t: LocaleObj;
  sort: ICommandSort;
  onView: (commandId?: string) => void;
}

export const getCommandColumns = ({ t, sort, onView }: IOptions): ColumnsType<Command> => {
  const sortOrderOf = (columnKey: SortColumnKey) => (sort.columnKey === columnKey ? sort.order : null);

  return [
    {
      title: t.INTEGRATION_FILE_NAME,
      dataIndex: 'fileName',
      key: 'fileName',
      width: COMMAND_COLUMN_WIDTH.fileName,
      render: (fileName: string | undefined, command: Command) =>
        fileName ? (
          <Button type="link" size="small" onClick={() => onView(command.id)}>
            {fileName}
          </Button>
        ) : (
          EMPTY_VALUE
        ),
    },
    {
      title: t.INTEGRATION_COMMAND_TYPE,
      dataIndex: 'commandType',
      key: 'commandType',
      width: COMMAND_COLUMN_WIDTH.commandType,
      sorter: true,
      sortOrder: sortOrderOf('commandType'),
      render: (commandType?: string) => commandType || EMPTY_VALUE,
    },
    {
      title: t.STATUS,
      dataIndex: 'status',
      key: 'status',
      width: COMMAND_COLUMN_WIDTH.status,
      sorter: true,
      sortOrder: sortOrderOf('status'),
      render: (status: CommandStatus | undefined, command: Command) => (
        <CommandStatusTag status={status} isHolding={command.isHolding} />
      ),
    },
    {
      title: t.INTEGRATION_ATTEMPTS,
      dataIndex: 'attempts',
      key: 'attempts',
      width: COMMAND_COLUMN_WIDTH.attempts,
      sorter: true,
      sortOrder: sortOrderOf('attempts'),
    },
    {
      title: t.CREATED,
      dataIndex: 'creationTime',
      key: 'creationTime',
      width: COMMAND_COLUMN_WIDTH.created,
      sorter: true,
      sortOrder: sortOrderOf('creationTime'),
      // Clearing the sort returns to this column descending, so it must come first in the cycle.
      sortDirections: ['descend', 'ascend'],
      render: (creationTime?: Moment) => formatDateTime(creationTime),
    },
    {
      title: t.INTEGRATION_NEXT_ATTEMPT,
      dataIndex: 'nextAttemptTime',
      key: 'nextAttemptTime',
      width: COMMAND_COLUMN_WIDTH.nextAttempt,
      sorter: true,
      sortOrder: sortOrderOf('nextAttemptTime'),
      render: (nextAttemptTime?: Moment) => formatDateTime(nextAttemptTime),
    },
    {
      title: t.INTEGRATION_HTTP,
      dataIndex: 'httpStatusCode',
      key: 'httpStatusCode',
      width: COMMAND_COLUMN_WIDTH.http,
      render: (httpStatusCode?: number) => httpStatusCode ?? EMPTY_VALUE,
    },
    {
      title: <InfoLabel label={t.INTEGRATION_LAST_FAILURE} hint={t.INTEGRATION_LAST_FAILURE_HINT} />,
      dataIndex: 'failureReason',
      key: 'failureReason',
      width: COMMAND_COLUMN_WIDTH.lastFailure,
      ellipsis: { showTitle: false },
      render: (failureReason?: string) =>
        failureReason ? (
          <Tooltip placement="topLeft" title={failureReason}>
            <span>{failureReason}</span>
          </Tooltip>
        ) : (
          EMPTY_VALUE
        ),
    },
    {
      title: t.ACTIONS,
      key: 'actions',
      width: COMMAND_COLUMN_WIDTH.actions,
      fixed: 'right',
      render: (_: unknown, command: Command) => (
        <Button type="link" size="small" onClick={() => onView(command.id)}>
          {t.VIEW}
        </Button>
      ),
    },
  ];
};
